import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, Download, Package, Trash2, Loader2, Scissors, Square,
  ImageIcon, Layers, Ruler, Zap, Lock, Check, MonitorSmartphone,
} from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { TShirtMockup, FABRICS } from "@/components/TShirtMockup";
import { getDeviceId } from "@/lib/device";
import Landing from "@/pages/Landing";
import PaymentStatus from "@/pages/PaymentStatus";
import Admin from "@/pages/Admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODES = [
  { key: "border", label: "Border Fade", icon: Square },
  { key: "cutout", label: "Subject Cutout", icon: Scissors },
];

function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-3.5 h-3.5 text-[#D4FF00]" strokeWidth={2.5} />
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
        {children}
      </span>
    </div>
  );
}

function Studio() {
  const [assets, setAssets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [processedPreview, setProcessedPreview] = useState(null);
  const [outDims, setOutDims] = useState(null);

  const [mode, setMode] = useState("border");
  const [feather, setFeather] = useState(20);
  const [canvas, setCanvas] = useState("original");
  const [customW, setCustomW] = useState(4500);
  const [customH, setCustomH] = useState(5400);

  const [fabric, setFabric] = useState("black");
  const [scale, setScale] = useState(62);
  const [view, setView] = useState("front");

  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [licensed, setLicensed] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [activating, setActivating] = useState(false);
  const [conflictKey, setConflictKey] = useState("");

  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Validate the device-locked license (key + this device) on mount.
  useEffect(() => {
    const key = localStorage.getItem("pf_license_key");
    if (!key) return;
    axios
      .post(`${API}/license/validate`, { license_key: key, device_id: getDeviceId() })
      .then((res) => setLicensed(!!res.data.valid))
      .catch(() => setLicensed(false));
  }, []);

  const startCheckout = async () => {
    setUnlocking(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "printfade_lifetime",
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setUnlocking(false);
      toast.error("Could not start checkout. Please try again.");
    }
  };

  const activateKey = async () => {
    const key = keyInput.trim();
    if (!key) return;
    setActivating(true);
    setConflictKey("");
    try {
      const res = await axios.post(`${API}/license/activate`, {
        license_key: key,
        device_id: getDeviceId(),
      });
      localStorage.setItem("pf_license_key", res.data.license_key);
      setLicensed(true);
      toast.success("License activated on this device.");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) {
        setConflictKey(key);
        toast.error("This key is active on another device. You can move it here.");
      } else if (code === 404) toast.error("License key not found.");
      else if (code === 403) toast.error("This license has been revoked.");
      else toast.error("Activation failed. Check the key and try again.");
    } finally {
      setActivating(false);
    }
  };

  const transferToThisDevice = async () => {
    if (!conflictKey) return;
    setActivating(true);
    try {
      const res = await axios.post(`${API}/license/reset`, {
        license_key: conflictKey,
        device_id: getDeviceId(),
      });
      localStorage.setItem("pf_license_key", res.data.license_key);
      setLicensed(true);
      setConflictKey("");
      toast.success("License moved to this device.");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 429) toast.error(e.response.data.detail || "Transfer is rate-limited. Try again later.");
      else if (code === 403) toast.error("This license has been revoked.");
      else toast.error("Transfer failed. Please try again.");
    } finally {
      setActivating(false);
    }
  };

  const params = useCallback(
    (id) => ({
      id,
      feather_pct: feather,
      mode,
      canvas,
      custom_w: customW,
      custom_h: customH,
      license_key: localStorage.getItem("pf_license_key") || null,
      device_id: getDeviceId(),
    }),
    [feather, mode, canvas, customW, customH]
  );

  // ---- Upload ----
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Please drop JPG or PNG images.");
      return;
    }
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    setUploading(true);
    try {
      const res = await axios.post(`${API}/upload`, form);
      const newAssets = res.data.assets;
      setAssets((prev) => [...prev, ...newAssets]);
      if (newAssets.length) setSelectedId(newAssets[0].id);
      toast.success(`${newAssets.length} image${newAssets.length > 1 ? "s" : ""} loaded`);
    } catch (e) {
      toast.error("Upload failed. Check the file format.");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAsset = (id) => {
    setAssets((prev) => prev.filter((a) => a.id !== id));
    if (selectedId === id) {
      setSelectedId((prev) => {
        const remaining = assets.filter((a) => a.id !== id);
        return remaining.length ? remaining[0].id : null;
      });
      setProcessedPreview(null);
    }
  };

  // ---- Live processing (debounced) ----
  useEffect(() => {
    if (!selectedId) {
      setProcessedPreview(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setProcessing(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/process`, params(selectedId));
        setProcessedPreview(res.data.preview);
        setOutDims({ w: res.data.width, h: res.data.height });
      } catch (e) {
        toast.error("Processing failed.");
      } finally {
        setProcessing(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [selectedId, feather, mode, canvas, customW, customH, params]);

  // ---- Downloads ----
  const triggerBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadOne = async () => {
    if (!selectedId) return;
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/download`, params(selectedId), { responseType: "blob" });
      const asset = assets.find((a) => a.id === selectedId);
      triggerBlob(res.data, `${(asset?.filename || "graphic").replace(/\.[^.]+$/, "")}_transparent.png`);
      toast.success("Transparent PNG downloaded (300 DPI)");
    } catch (e) {
      if (e?.response?.status === 402) {
        setLicensed(false);
        toast.error("Purchase a license to export.");
      } else {
        toast.error("Download failed.");
      }
    } finally {
      setDownloading(false);
    }
  };

  const downloadZip = async () => {
    if (!assets.length) return;
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/download-zip`,
        { ids: assets.map((a) => a.id), feather_pct: feather, mode, canvas, custom_w: customW, custom_h: customH, license_key: localStorage.getItem("pf_license_key") || null, device_id: getDeviceId() },
        { responseType: "blob" }
      );
      triggerBlob(res.data, "tshirt_graphics.zip");
      toast.success(`ZIP with ${assets.length} PNGs downloaded`);
    } catch (e) {
      if (e?.response?.status === 402) {
        setLicensed(false);
        toast.error("Purchase a license to export.");
      } else {
        toast.error("ZIP export failed.");
      }
    } finally {
      setDownloading(false);
    }
  };

  const selectedAsset = assets.find((a) => a.id === selectedId);

  return (
    <div className="App noise-overlay bg-[#0A0A0A] text-white flex flex-col h-screen overflow-hidden">
      <Toaster position="top-center" theme="dark" />

      {/* Top bar */}
      <header className="h-14 shrink-0 border-b border-[#262626] flex items-center justify-between px-5 bg-[#0A0A0A]">
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="studio-home-link" className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#D4FF00] flex items-center justify-center">
              <Layers className="w-4 h-4 text-black" strokeWidth={2.5} />
            </div>
            <h1 className="font-head font-extrabold text-lg tracking-tighter">
              Print Fade <span className="text-[#D4FF00]">Studio</span>
            </h1>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] hidden sm:inline border border-[#262626] px-2 py-0.5">
            T-SHIRT GRAPHIC PREP
          </span>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">
          <Zap className="w-3.5 h-3.5 text-[#D4FF00]" /> 300 DPI · TRANSPARENT PNG
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ---------- LEFT: Assets ---------- */}
        <aside className="w-[260px] shrink-0 border-r border-[#262626] bg-[#0d0d0d] flex flex-col">
          <div className="p-4 border-b border-[#262626]">
            <SectionLabel icon={ImageIcon}>Asset Queue</SectionLabel>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="dropzone"
              className={`cursor-pointer border border-dashed p-6 flex flex-col items-center justify-center gap-2 text-center transition-colors ${
                dragOver ? "border-[#D4FF00] bg-[#D4FF00]/5" : "border-[#333] hover:border-[#555] hover:bg-white/5"
              }`}
            >
              {uploading ? (
                <Loader2 className="w-6 h-6 text-[#D4FF00] animate-spin" />
              ) : (
                <Upload className="w-6 h-6 text-[#A3A3A3]" />
              )}
              <p className="text-sm font-medium">Drop images</p>
              <p className="font-mono text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                JPG / PNG · multi-file
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="file-input"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            />
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="asset-list">
            {assets.length === 0 && (
              <p className="text-center text-[#555] text-xs font-mono mt-8 px-4">
                No assets yet. Upload to begin.
              </p>
            )}
            {assets.map((a) => (
              <div
                key={a.id}
                onClick={() => setSelectedId(a.id)}
                data-testid={`asset-${a.id}`}
                className={`group relative flex items-center gap-3 p-2 border cursor-pointer transition-colors ${
                  selectedId === a.id
                    ? "border-[#D4FF00] bg-[#D4FF00]/5"
                    : "border-[#262626] hover:border-[#444] hover:bg-white/5"
                }`}
              >
                <div className="w-12 h-12 shrink-0 checkerboard flex items-center justify-center overflow-hidden">
                  <img src={a.preview} alt={a.filename} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{a.filename}</p>
                  <p className="font-mono text-[10px] text-[#A3A3A3]">{a.width}×{a.height}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeAsset(a.id); }}
                  data-testid={`remove-${a.id}`}
                  className="opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-[#FF3B30] transition-opacity"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* ---------- CENTER: Canvas ---------- */}
        <main className="flex-1 flex flex-col overflow-hidden relative bg-[#0A0A0A]">
          <div
            className="flex-1 flex flex-col items-center justify-center p-8 relative"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)",
              backgroundSize: "26px 26px",
            }}
          >
            {!selectedAsset ? (
              <div className="text-center">
                <Layers className="w-12 h-12 text-[#262626] mx-auto mb-4" />
                <p className="font-head text-2xl tracking-tighter text-[#333]">Upload a graphic to start</p>
                <p className="font-mono text-xs text-[#444] mt-2 uppercase tracking-widest">
                  Feather · Preview · Export
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6 flex items-center border border-[#262626]" data-testid="view-toggle">
                  {["front", "back"].map((v) => (
                    <button
                      key={v}
                      onClick={() => setView(v)}
                      data-testid={`view-${v}`}
                      className={`px-6 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                        view === v ? "bg-[#D4FF00] text-black" : "text-[#A3A3A3] hover:bg-white/5"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <TShirtMockup
                    fabric={fabric}
                    graphicUrl={processedPreview}
                    scale={scale}
                    view={view}
                    resetKey={`${selectedId}-${view}`}
                  />
                  <AnimatePresence>
                    {processing && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute top-3 right-3 flex items-center gap-2 bg-black/70 border border-[#262626] px-3 py-1.5"
                      >
                        <Loader2 className="w-3.5 h-3.5 text-[#D4FF00] animate-spin" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                          {mode === "cutout" ? "Cutting out" : "Feathering"}
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Fabric swatches */}
                <div className="mt-8 flex items-center gap-3" data-testid="fabric-swatches">
                  {Object.entries(FABRICS).map(([key, f]) => (
                    <button
                      key={key}
                      onClick={() => setFabric(key)}
                      data-testid={`fabric-${key}`}
                      title={f.name}
                      className={`w-9 h-9 border-2 transition-transform hover:scale-110 ${
                        fabric === key ? "border-[#D4FF00] scale-110" : "border-[#333]"
                      }`}
                      style={{ backgroundColor: f.fill }}
                    />
                  ))}
                </div>

                {/* Scale control */}
                <div className="mt-6 w-full max-w-[360px]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                      Graphic Scale
                    </span>
                    <span className="font-mono text-[11px] text-[#D4FF00]">{scale}%</span>
                  </div>
                  <Slider
                    value={[scale]}
                    onValueChange={(v) => setScale(v[0])}
                    min={20}
                    max={100}
                    step={1}
                    data-testid="scale-slider"
                  />
                  <p className="font-mono text-[9px] text-[#555] mt-2 text-center uppercase tracking-widest">
                    Drag the graphic on the shirt to reposition
                  </p>
                </div>
              </>
            )}
          </div>
        </main>

        {/* ---------- RIGHT: Inspector ---------- */}
        <aside className="w-[320px] shrink-0 border-l border-[#262626] bg-[#0d0d0d] overflow-y-auto">
          <div className="p-5 space-y-7">
            {/* Feather mode */}
            <div>
              <SectionLabel icon={Scissors}>Feather Mode</SectionLabel>
              <div className="grid grid-cols-2 gap-0 border border-[#262626]">
                {MODES.map((m) => {
                  const Icon = m.icon;
                  const active = mode === m.key;
                  return (
                    <button
                      key={m.key}
                      onClick={() => setMode(m.key)}
                      data-testid={`mode-${m.key}`}
                      className={`flex items-center justify-center gap-2 py-3 text-xs font-semibold transition-colors ${
                        active ? "bg-[#D4FF00] text-black" : "bg-transparent text-[#A3A3A3] hover:bg-white/5"
                      }`}
                    >
                      <Icon className="w-4 h-4" strokeWidth={2.5} />
                      {m.label}
                    </button>
                  );
                })}
              </div>
              <p className="font-mono text-[10px] text-[#555] mt-2 leading-relaxed">
                {mode === "cutout"
                  ? "Auto-detects the subject and feathers the cutout edge."
                  : "Fades the outer image perimeter to transparent."}
              </p>
            </div>

            {/* Feather width */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <SectionLabel icon={Layers}>Feather Width</SectionLabel>
                <span className="font-mono text-sm text-[#D4FF00]" data-testid="feather-value">{feather}%</span>
              </div>
              <Slider
                value={[feather]}
                onValueChange={(v) => setFeather(v[0])}
                min={1}
                max={100}
                step={1}
                data-testid="feather-slider"
              />
              <div className="flex justify-between font-mono text-[9px] text-[#555] mt-2">
                <span>1%</span><span>100%</span>
              </div>
            </div>

            {/* Canvas preset */}
            <div>
              <SectionLabel icon={Ruler}>Canvas Preset</SectionLabel>
              <Select value={canvas} onValueChange={setCanvas}>
                <SelectTrigger data-testid="canvas-select" className="rounded-none border-[#262626] bg-[#141414] font-mono text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] text-white">
                  <SelectItem value="original">Original Aspect Ratio</SelectItem>
                  <SelectItem value="pod">POD Standard · 4500×5400</SelectItem>
                  <SelectItem value="custom">Custom Size</SelectItem>
                </SelectContent>
              </Select>
              {canvas === "custom" && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <label className="font-mono text-[9px] text-[#555] uppercase">Width</label>
                    <Input
                      type="number"
                      value={customW}
                      onChange={(e) => setCustomW(Number(e.target.value))}
                      data-testid="custom-width"
                      className="rounded-none border-[#262626] bg-[#141414] font-mono text-xs mt-1"
                    />
                  </div>
                  <div>
                    <label className="font-mono text-[9px] text-[#555] uppercase">Height</label>
                    <Input
                      type="number"
                      value={customH}
                      onChange={(e) => setCustomH(Number(e.target.value))}
                      data-testid="custom-height"
                      className="rounded-none border-[#262626] bg-[#141414] font-mono text-xs mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Transparency preview + output */}
            <div>
              <SectionLabel icon={ImageIcon}>Transparent Output</SectionLabel>
              <div className="checkerboard border border-[#262626] aspect-square flex items-center justify-center overflow-hidden" data-testid="output-preview">
                {processedPreview ? (
                  <img src={processedPreview} alt="output" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="font-mono text-[10px] text-[#555] uppercase tracking-widest">No output</span>
                )}
              </div>
              {outDims && (
                <div className="flex items-center justify-between mt-2 font-mono text-[10px] text-[#A3A3A3]">
                  <span data-testid="output-dims">{outDims.w}×{outDims.h}px</span>
                  <span className="text-[#D4FF00]">300 DPI · RGBA</span>
                </div>
              )}
            </div>

            {/* Export */}
            <div className="space-y-2 pt-2" data-testid="export-section">
              {licensed ? (
                <>
                  <Button
                    onClick={downloadOne}
                    disabled={!selectedId || downloading}
                    data-testid="download-png-btn"
                    className="w-full rounded-none bg-[#D4FF00] text-black hover:bg-white font-bold h-11 gap-2"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={2.5} />}
                    Download Transparent PNG
                  </Button>
                  <Button
                    onClick={downloadZip}
                    disabled={!assets.length || downloading}
                    data-testid="download-zip-btn"
                    variant="outline"
                    className="w-full rounded-none border-[#333] bg-transparent text-white hover:bg-white hover:text-black font-semibold h-11 gap-2"
                  >
                    <Package className="w-4 h-4" strokeWidth={2.5} />
                    Download All ({assets.length}) as ZIP
                  </Button>
                  <div className="flex items-center justify-center gap-1.5 pt-1 font-mono text-[10px] uppercase tracking-widest text-[#D4FF00]">
                    <Check className="w-3 h-3" strokeWidth={3} /> Licensed · Device-Locked
                  </div>
                </>
              ) : (
                <div className="border border-[#D4FF00]/40 bg-[#D4FF00]/[0.04] p-4" data-testid="export-locked">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Lock className="w-4 h-4 text-[#D4FF00]" strokeWidth={2.5} />
                    <span className="font-head font-bold text-sm">Export Locked</span>
                  </div>
                  <p className="font-mono text-[10px] text-[#A3A3A3] leading-relaxed mb-3">
                    Preview is free. Unlock unlimited 300 DPI PNG &amp; ZIP exports with a one-time license — locked to this device.
                  </p>
                  <Button
                    onClick={startCheckout}
                    disabled={unlocking}
                    data-testid="unlock-export-btn"
                    className="w-full rounded-none bg-[#D4FF00] text-black hover:bg-white font-bold h-11 gap-2"
                  >
                    {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" strokeWidth={2.5} />}
                    Unlock 300 DPI Export – $9.95
                  </Button>

                  <div className="mt-4 pt-4 border-t border-[#262626]">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#666] mb-2">
                      Already have a key?
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                        placeholder="PFS-XXXX-XXXX-XXXX"
                        data-testid="license-key-input"
                        className="rounded-none border-[#262626] bg-[#141414] font-mono text-xs h-10"
                      />
                      <Button
                        onClick={activateKey}
                        disabled={activating || !keyInput.trim()}
                        data-testid="activate-key-btn"
                        variant="outline"
                        className="rounded-none border-[#333] bg-transparent text-white hover:bg-white hover:text-black font-semibold h-10 px-3 shrink-0"
                      >
                        {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate"}
                      </Button>
                    </div>
                    {conflictKey && (
                      <div className="mt-3" data-testid="transfer-prompt">
                        <p className="font-mono text-[10px] text-[#FF3B30] leading-relaxed mb-2">
                          This key is active on another computer. Move it to this device? (Your other device will be deactivated.)
                        </p>
                        <Button
                          onClick={transferToThisDevice}
                          disabled={activating}
                          data-testid="transfer-device-btn"
                          className="w-full rounded-none bg-[#D4FF00] text-black hover:bg-white font-bold h-10 gap-2"
                        >
                          {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MonitorSmartphone className="w-4 h-4" strokeWidth={2.5} />}
                          Move License to This Device
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/payment/success" element={<PaymentStatus />} />
        <Route path="/payment/cancel" element={<PaymentStatus />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}
